import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { CreateButton, DeleteConfirmation, ListView, ListViewActions, ListViewMenu, Page, Tooltip } from '~/components';
import { useApi, useConfirmation, useToast, useWorkspace } from '~/contexts';
import { useAuth, useDateTimeFormat } from '~/hooks';
import { ErrorPage, PageLoader } from '~/routes/public/pages';
import { colors, weights } from '~/styles';
import { dateFormats } from '~/utils';
import RateCardForm from './RateCardForm';
import { coveringCard, isFirstCard } from './timeline';

// The project's rate cards, oldest first. Every date on the project is covered
// by exactly one of them, so the list reads as an unbroken timeline.

const Tag = styled.span`
  display: inline-block;
  margin-right: 0.5rem;
  padding: 0.125rem 0.5rem;
  border-radius: 999rem;
  font-size: 0.75rem;
  font-weight: ${weights.bold};
  color: ${({ $muted }) => ($muted ? colors.grey55 : colors.white)};
  background-color: ${({ $muted }) => ($muted ? colors.grey5 : colors.primary)};
`;

const Notes = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export default function ProjectRateCardsTab({ project }) {
  const api = useApi();
  const auth = useAuth();
  const { workspace } = useWorkspace();
  const toast = useToast();
  const confirmation = useConfirmation();
  const dateTimeFormat = useDateTimeFormat();

  const [query, setQuery] = useState({ isReady: false, data: null, error: null });
  const [dialog, setDialog] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.www.workspaces(workspace.id).projectSettings(project.id).rateCards().get();
      setQuery({ isReady: true, data, error: null });
    } catch (error) {
      setQuery({ isReady: true, data: null, error });
    }
  }, [api, workspace.id, project.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const closeDialog = () => setDialog(null);

  const handleSaved = async () => {
    await fetchData();
    closeDialog();
  };

  const handleOpenForm = (rateCard = null) => {
    setDialog(
      <RateCardForm
        project={project}
        rateCards={query.data}
        rateCardId={rateCard?.id}
        readOnly={!canEdit}
        onSaved={handleSaved}
        onClose={closeDialog}
      />,
    );
  };

  const handleDelete = async (rateCard) => {
    await confirmation.prompt((resolve) => (
      <DeleteConfirmation
        title="Delete Rate Card"
        resolve={async (result) => {
          if (result) {
            try {
              await api.www.workspaces(workspace.id).projectSettings(project.id).rateCards(rateCard.id).delete();
              toast.success('Rate card deleted successfully.');
              await fetchData();
            } catch (error) {
              toast.error(error.message);
            }
          }
          resolve(result);
        }}>
        Are you sure you want to delete this rate card? The previous rate card will be extended through this period.
      </DeleteConfirmation>
    ));
  };

  if (!query.isReady) return <PageLoader />;
  if (query.error) return <ErrorPage publicSite={false} />;

  const data = query.data;
  const canEdit = project.permissions.edit && auth.projects.sections.billing;
  const currentCard = coveringCard(data, moment().format(dateFormats.isoDate));

  return (
    <>
      <Page.TabHeader>
        <Page.Title>Rate Cards</Page.Title>

        {canEdit && (
          <Page.Actions>
            <CreateButton tooltip="New Rate Card" onClick={() => handleOpenForm()} data-testid="rate_card_add_button" />
          </Page.Actions>
        )}
      </Page.TabHeader>

      <Page.ListView>
        <ListView data-testid="table">
          <ListView.Header>
            <ListView.Column width="4rem" align="center">
              #
            </ListView.Column>
            <ListView.Column width="10rem">Starts On</ListView.Column>
            <ListView.Column width="10rem">Ends On</ListView.Column>
            <ListView.Column minWidth="16rem">Notes</ListView.Column>
            <ListView.Column width="12rem" />
            <ListViewActions.Column />
          </ListView.Header>

          <ListView.Body>
            {data.map((rateCard) => {
              const isFirst = isFirstCard(rateCard);

              return (
                <ListView.Row key={rateCard.id} data-testid="rate_card_row" onClick={() => handleOpenForm(rateCard)}>
                  <ListView.Cell>{rateCard.number}</ListView.Cell>
                  <ListView.Cell>{isFirst ? 'Project start' : dateTimeFormat.format(rateCard.start)}</ListView.Cell>
                  <ListView.Cell>{rateCard.end ? dateTimeFormat.format(rateCard.end) : 'Ongoing'}</ListView.Cell>
                  <ListView.Cell>
                    <Tooltip message={rateCard.notes} Container={Notes}>
                      {rateCard.notes}
                    </Tooltip>
                  </ListView.Cell>
                  <ListView.Cell>
                    {rateCard === currentCard && <Tag>Current</Tag>}
                    {rateCard.isBudgetRateCard && <Tag $muted>Budget</Tag>}
                  </ListView.Cell>
                  <ListViewActions>
                    {canEdit ? (
                      <>
                        <ListViewActions.Edit onClick={() => handleOpenForm(rateCard)} />
                        <hr />
                        <ListViewMenu>
                          <ListViewMenu.Item onClick={() => handleOpenForm(rateCard)}>Edit</ListViewMenu.Item>
                          <ListViewMenu.DeleteItem
                            disabled={isFirst || rateCard.isBudgetRateCard}
                            tooltip={
                              isFirst
                                ? 'The first rate card cannot be deleted.'
                                : rateCard.isBudgetRateCard
                                  ? 'This rate card is used by the project budget.'
                                  : undefined
                            }
                            onClick={() => handleDelete(rateCard)}>
                            Delete
                          </ListViewMenu.DeleteItem>
                        </ListViewMenu>
                      </>
                    ) : (
                      <ListViewActions.View onClick={() => handleOpenForm(rateCard)} />
                    )}
                  </ListViewActions>
                </ListView.Row>
              );
            })}

            {data.length === 0 && <ListView.Empty />}
          </ListView.Body>

          <ListView.Status total={data.length} label="Rate Card" />
        </ListView>
      </Page.ListView>

      {dialog}
    </>
  );
}
